import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { journalEntriesAPI, accountsAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Filter,
  CheckCircle,
  Send,
  Trash2,
  Eye,
  MoreHorizontal,
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value || 0);
};

function StatusBadge({ status }) {
  const styles = {
    draft: 'status-draft',
    submitted: 'status-pending',
    approved: 'status-approved',
    posted: 'status-posted',
    rejected: 'status-rejected',
  };

  return (
    <span className={`status-badge ${styles[status?.toLowerCase()] || 'status-draft'}`}>
      {status}
    </span>
  );
}

export default function JournalEntries() {
  const { selectedCompany } = useCompany();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    description: '',
    reference: '',
    lines: [{ account_id: '', description: '', debit: 0, credit: 0 }],
  });

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['journal-entries', selectedCompany?.id],
    queryFn: () => journalEntriesAPI.getAll(selectedCompany?.id).then((res) => res.data),
    enabled: !!selectedCompany?.id,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts', selectedCompany?.id],
    queryFn: () => accountsAPI.getAll(selectedCompany?.id).then((res) => res.data),
    enabled: !!selectedCompany?.id,
  });

  const createMutation = useMutation({
    mutationFn: (data) => journalEntriesAPI.create({ ...data, company_id: selectedCompany?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries(['journal-entries']);
      toast.success('Journal entry created successfully');
      setShowForm(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create journal entry');
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id) => journalEntriesAPI.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['journal-entries']);
      toast.success('Journal entry approved');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to approve entry');
    },
  });

  const postMutation = useMutation({
    mutationFn: (id) => journalEntriesAPI.post(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['journal-entries']);
      queryClient.invalidateQueries(['accounts']);
      toast.success('Journal entry posted to ledger');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to post entry');
    },
  });

  const resetForm = () => {
    setFormData({
      entry_date: new Date().toISOString().split('T')[0],
      description: '',
      reference: '',
      lines: [{ account_id: '', description: '', debit: 0, credit: 0 }],
    });
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { account_id: '', description: '', debit: 0, credit: 0 }],
    });
  };

  const removeLine = (index) => {
    setFormData({
      ...formData,
      lines: formData.lines.filter((_, i) => i !== index),
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    
    // Auto-clear the opposite field when entering a value
    if (field === 'debit' && value > 0) {
      newLines[index].credit = 0;
    } else if (field === 'credit' && value > 0) {
      newLines[index].debit = 0;
    }
    
    setFormData({ ...formData, lines: newLines });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const totalDebit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
    const totalCredit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
    
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      toast.error('Debits must equal credits');
      return;
    }
    
    if (formData.lines.some((line) => !line.account_id)) {
      toast.error('Please select an account for each line');
      return;
    }
    
    createMutation.mutate(formData);
  };

  const totalDebit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
  const totalCredit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const filteredEntries = entries.filter(
    (entry) =>
      entry.entry_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="journal-entries-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Journal Entries</h1>
          <p className="text-muted-foreground mt-1">
            Record transactions for {selectedCompany?.name || 'your company'}
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          data-testid="new-journal-entry-button"
        >
          <Plus className="h-4 w-4" />
          New Entry
        </button>
      </div>

      {/* Search */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search entries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="search-journal-entries"
          />
        </div>
        <button className="inline-flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted transition-colors">
          <Filter className="h-4 w-4" />
          Filters
        </button>
      </div>

      {/* Journal Entry Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-3xl mx-4 animate-fade-in">
            <div className="p-6 border-b border-border">
              <h2 className="text-lg font-semibold">New Journal Entry</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Entry Header */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Entry Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.entry_date}
                    onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    data-testid="entry-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Reference</label>
                  <input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    placeholder="e.g., INV-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description *</label>
                  <input
                    type="text"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    placeholder="Entry description"
                    data-testid="entry-description-input"
                  />
                </div>
              </div>

              {/* Journal Lines */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium">Journal Lines</label>
                  <button
                    type="button"
                    onClick={addLine}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add Line
                  </button>
                </div>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="table-header">
                        <th className="text-left p-3">Account</th>
                        <th className="text-left p-3 hidden sm:table-cell">Description</th>
                        <th className="text-right p-3 w-28">Debit</th>
                        <th className="text-right p-3 w-28">Credit</th>
                        <th className="p-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.lines.map((line, index) => (
                        <tr key={index} className="border-t border-border">
                          <td className="p-2">
                            <select
                              value={line.account_id}
                              onChange={(e) => updateLine(index, 'account_id', e.target.value)}
                              className="w-full px-2 py-1.5 rounded border border-input bg-background text-sm"
                              data-testid={`line-account-${index}`}
                            >
                              <option value="">Select account</option>
                              {accounts.map((acc) => (
                                <option key={acc.id} value={acc.id}>
                                  {acc.code} - {acc.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-2 hidden sm:table-cell">
                            <input
                              type="text"
                              value={line.description}
                              onChange={(e) => updateLine(index, 'description', e.target.value)}
                              className="w-full px-2 py-1.5 rounded border border-input bg-background text-sm"
                              placeholder="Line description"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={line.debit || ''}
                              onChange={(e) => updateLine(index, 'debit', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 rounded border border-input bg-background text-sm text-right font-mono"
                              data-testid={`line-debit-${index}`}
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={line.credit || ''}
                              onChange={(e) => updateLine(index, 'credit', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 rounded border border-input bg-background text-sm text-right font-mono"
                              data-testid={`line-credit-${index}`}
                            />
                          </td>
                          <td className="p-2">
                            {formData.lines.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLine(index)}
                                className="p-1 text-muted-foreground hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border bg-muted/50">
                        <td colSpan={2} className="p-3 text-right font-medium">Totals:</td>
                        <td className="p-3 text-right font-mono font-semibold">{formatCurrency(totalDebit)}</td>
                        <td className="p-3 text-right font-mono font-semibold">{formatCurrency(totalCredit)}</td>
                        <td className="p-3">
                          {isBalanced ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <span className="text-xs text-destructive">Unbalanced</span>
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-input rounded-lg hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || !isBalanced}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                  data-testid="save-journal-entry-button"
                >
                  {createMutation.isPending ? 'Saving...' : 'Save Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Entries Table */}
      <div className="card-surface overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="table-header">
              <th className="text-left p-4">Entry #</th>
              <th className="text-left p-4 hidden sm:table-cell">Date</th>
              <th className="text-left p-4">Description</th>
              <th className="text-right p-4 hidden md:table-cell">Debit</th>
              <th className="text-right p-4 hidden md:table-cell">Credit</th>
              <th className="text-left p-4">Status</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="p-4"><div className="h-4 w-20 bg-muted rounded animate-pulse"></div></td>
                  <td className="p-4 hidden sm:table-cell"><div className="h-4 w-24 bg-muted rounded animate-pulse"></div></td>
                  <td className="p-4"><div className="h-4 w-40 bg-muted rounded animate-pulse"></div></td>
                  <td className="p-4 hidden md:table-cell"><div className="h-4 w-20 bg-muted rounded animate-pulse"></div></td>
                  <td className="p-4 hidden md:table-cell"><div className="h-4 w-20 bg-muted rounded animate-pulse"></div></td>
                  <td className="p-4"><div className="h-5 w-16 bg-muted rounded animate-pulse"></div></td>
                  <td className="p-4"></td>
                </tr>
              ))
            ) : filteredEntries.length > 0 ? (
              filteredEntries.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-t border-border hover:bg-muted/50 transition-colors"
                  data-testid={`journal-entry-row-${entry.id}`}
                >
                  <td className="p-4 font-mono text-sm font-medium">{entry.entry_number}</td>
                  <td className="p-4 text-sm hidden sm:table-cell">{entry.entry_date}</td>
                  <td className="p-4">
                    <p className="font-medium truncate max-w-[200px]">{entry.description}</p>
                    {entry.reference && (
                      <p className="text-xs text-muted-foreground">Ref: {entry.reference}</p>
                    )}
                  </td>
                  <td className="p-4 text-right font-mono hidden md:table-cell">
                    {formatCurrency(entry.total_debit)}
                  </td>
                  <td className="p-4 text-right font-mono hidden md:table-cell">
                    {formatCurrency(entry.total_credit)}
                  </td>
                  <td className="p-4">
                    <StatusBadge status={entry.status} />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      {entry.status === 'draft' && (
                        <button
                          onClick={() => approveMutation.mutate(entry.id)}
                          className="p-2 hover:bg-muted rounded-lg text-green-600"
                          title="Approve"
                          disabled={approveMutation.isPending}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                      )}
                      {entry.status === 'approved' && (
                        <button
                          onClick={() => postMutation.mutate(entry.id)}
                          className="p-2 hover:bg-muted rounded-lg text-blue-600"
                          title="Post to Ledger"
                          disabled={postMutation.isPending}
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      )}
                      <button className="p-2 hover:bg-muted rounded-lg text-muted-foreground">
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="p-8 text-center text-muted-foreground">
                  {searchTerm ? 'No entries found matching your search' : 'No journal entries yet. Create your first entry!'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <p>Showing {filteredEntries.length} of {entries.length} entries</p>
      </div>
    </div>
  );
}
